import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Button, Box, Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteIcon from '@mui/icons-material/Delete';

export interface DeleteConfirmOptions {
  title?: string;
  message: string;
  warning?: string;
  confirmLabel?: string;
}

interface DeleteConfirmContextType {
  confirmDelete: (options: DeleteConfirmOptions) => Promise<boolean>;
}

const DeleteConfirmContext = createContext<DeleteConfirmContextType | null>(null);

export const useDeleteConfirm = (): DeleteConfirmContextType => {
  const ctx = useContext(DeleteConfirmContext);
  if (!ctx) throw new Error('useDeleteConfirm must be used inside <DeleteConfirmProvider>');
  return ctx;
};

export const DeleteConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<DeleteConfirmOptions | null>(null);
  const resolveRef = useRef<((v: boolean) => void) | null>(null);

  const confirmDelete = useCallback((options: DeleteConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setOpts(options);
      setOpen(true);
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = () => {
    resolveRef.current?.(true);
    resolveRef.current = null;
    setOpen(false);
    setOpts(null);
  };

  const handleCancel = () => {
    resolveRef.current?.(false);
    resolveRef.current = null;
    setOpen(false);
    setOpts(null);
  };

  return (
    <DeleteConfirmContext.Provider value={{ confirmDelete }}>
      {children}

      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ elevation: 8 }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <WarningAmberIcon color="error" sx={{ flexShrink: 0 }} />
          <Typography variant="h6" component="span" fontWeight={700}>
            {opts?.title ?? 'Confirm Delete'}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pb: opts?.warning ? 1 : 2 }}>
          <DialogContentText sx={{ color: 'text.primary' }}>
            {opts?.message}
          </DialogContentText>

          {opts?.warning && (
            <Box
              sx={{
                mt: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: 'error.main',
                opacity: 0.85,
              }}
            >
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>
                {opts.warning}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            color="inherit"
            sx={{ minWidth: 90 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{ minWidth: 110 }}
          >
            {opts?.confirmLabel ?? 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </DeleteConfirmContext.Provider>
  );
};
